import { useState, useEffect, useCallback } from 'react';
import { useTheme, useMediaQuery } from '@mui/material';

export type LayoutMode = '1-pane' | '2-pane' | '3-pane';
export type ActivePane = 'left' | 'center' | 'right';

export interface ResponsiveLayoutConfig {
  mode: LayoutMode;
  activePane: ActivePane;
  showLeft: boolean;
  showCenter: boolean;
  showRight: boolean;
  leftWidth: number;
  rightWidth: number;
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
}

export interface UseResponsiveLayoutProps {
  defaultLeftWidth?: number;
  defaultRightWidth?: number;
  minLeftWidth?: number;
  maxLeftWidth?: number;
  minRightWidth?: number;
  maxRightWidth?: number;
}

export const useResponsiveLayout = ({
  defaultLeftWidth = 280,
  defaultRightWidth = 320,
  minLeftWidth = 200,
  maxLeftWidth = 400,
  minRightWidth = 250,
  maxRightWidth = 500,
}: UseResponsiveLayoutProps = {}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // < 900px
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg')); // 900px - 1200px
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg')); // >= 1200px

  const [leftWidth, setLeftWidth] = useState(defaultLeftWidth);
  const [rightWidth, setRightWidth] = useState(defaultRightWidth);
  const [activePane, setActivePane] = useState<ActivePane>('center');

  // レイアウト設定を計算
  const getLayoutConfig = useCallback((): ResponsiveLayoutConfig => {
    if (isMobile) {
      // モバイル: 1ペイン表示
      return {
        mode: '1-pane',
        activePane,
        showLeft: activePane === 'left',
        showCenter: activePane === 'center',
        showRight: activePane === 'right',
        leftWidth: window.innerWidth,
        rightWidth: window.innerWidth,
        isMobile: true,
        isTablet: false,
        isDesktop: false,
      };
    } else if (isTablet) {
      // タブレット: 2ペイン表示
      const centerActive = activePane === 'center';
      return {
        mode: '2-pane',
        activePane,
        showLeft: activePane === 'left' || centerActive,
        showCenter: true,
        showRight: activePane === 'right' || centerActive,
        leftWidth: activePane === 'right' ? 0 : leftWidth,
        rightWidth: activePane === 'left' ? 0 : rightWidth,
        isMobile: false,
        isTablet: true,
        isDesktop: false,
      };
    } else {
      // デスクトップ: 3ペイン表示
      return {
        mode: '3-pane',
        activePane: 'center',
        showLeft: true,
        showCenter: true,
        showRight: true,
        leftWidth,
        rightWidth,
        isMobile: false,
        isTablet: false,
        isDesktop: true,
      };
    }
  }, [isMobile, isTablet, isDesktop, activePane, leftWidth, rightWidth]);

  const layoutConfig = getLayoutConfig();

  // アクティブペインの切り替え（モバイル・タブレット用）
  const switchToPane = useCallback((pane: ActivePane) => {
    if (isDesktop) return; // デスクトップでは無効
    setActivePane(pane);
  }, [isDesktop]);

  // 左サイドバーの幅調整
  const setLeftPaneWidth = useCallback((width: number) => {
    const constrainedWidth = Math.max(minLeftWidth, Math.min(maxLeftWidth, width));
    setLeftWidth(constrainedWidth);
  }, [minLeftWidth, maxLeftWidth]);

  // 右サイドバーの幅調整
  const setRightPaneWidth = useCallback((width: number) => {
    const constrainedWidth = Math.max(minRightWidth, Math.min(maxRightWidth, width));
    setRightWidth(constrainedWidth);
  }, [minRightWidth, maxRightWidth]);

  // ペインの表示/非表示切り替え（デスクトップ用）
  const toggleLeftPane = useCallback(() => {
    if (!isDesktop) return;
    setLeftWidth(prev => prev === 0 ? defaultLeftWidth : 0);
  }, [isDesktop, defaultLeftWidth]);

  const toggleRightPane = useCallback(() => {
    if (!isDesktop) return;
    setRightWidth(prev => prev === 0 ? defaultRightWidth : 0);
  }, [isDesktop, defaultRightWidth]);

  // ブレークポイント変更時にアクティブペインをリセット
  useEffect(() => {
    if (isDesktop) {
      setActivePane('center');
    }
  }, [isDesktop]);

  // リサイズ対応
  const createResizeHandler = useCallback((
    side: 'left' | 'right',
    currentWidth: number,
    setWidth: (width: number) => void
  ) => {
    return (event: React.MouseEvent) => {
      if (!isDesktop) return; // デスクトップ以外では無効

      const startX = event.clientX;
      const startWidth = currentWidth;

      const handleMouseMove = (e: MouseEvent) => {
        const deltaX = e.clientX - startX;
        const newWidth = side === 'left' 
          ? startWidth + deltaX 
          : startWidth - deltaX;
        setWidth(newWidth);
      };

      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    };
  }, [isDesktop]);

  return {
    // レイアウト設定
    layoutConfig,
    
    // ペイン制御
    activePane,
    switchToPane,
    
    // 幅制御
    leftWidth,
    rightWidth,
    setLeftPaneWidth,
    setRightPaneWidth,
    
    // 表示制御
    toggleLeftPane,
    toggleRightPane,
    
    // リサイズハンドラー
    createLeftResizeHandler: () => createResizeHandler('left', leftWidth, setLeftPaneWidth),
    createRightResizeHandler: () => createResizeHandler('right', rightWidth, setRightPaneWidth),
    
    // ブレークポイント情報
    isMobile,
    isTablet,
    isDesktop,
  };
};

export default useResponsiveLayout;




